// A workspace's console at `/<slug>/…` (02-DESIGN/03-console/01-the-console.md
// § the address, § signing in): the slug in the address is the sign-in hint;
// the tenant profile is read; a person arriving signed out sees the
// workspace's page and lands back on this address after the flow; a person
// signed in is placed and sees the shell. The states between: signed in
// elsewhere, entering, and a refusal at the callout as *Not allowed*.

import { useEffect } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router";

import { parseAddress, tenantPath } from "../../address/address.ts";
import { consoleAddress, PLATFORM_NAME, providerName, workspaceName } from "../../platform/names.ts";
import { readTenant, type TenantProfile } from "../../platform/profile.ts";
import {
  forgetMemberships,
  keepMemberships,
  keptMemberships,
  readMemberships,
} from "../../platform/moves.ts";
import { refusalSentence, type Refusal } from "../../platform/refusal.ts";
import { ConnectionProvider, useBus, usePage } from "../../platform/session.tsx";
import { Shell } from "../../shell/Shell.tsx";
import { begin, endSessionUrl, providerOf } from "../../signin/flow.ts";
import { choices } from "../../signin/provider.ts";
import { loadSession } from "../../signin/tokens.ts";
import { withForm } from "../../ui/form.tsx";
import { Button, describe, useLoad } from "../../ui/components.tsx";
import { Entry } from "../entry/Entry.tsx";
import { CREATE_FORM } from "../entry/ChooseWorkspace.tsx";
import { withPlaneAsk } from "../entry/plane-ask.ts";
import { WorkspaceSignIn } from "../entry/WorkspaceSignIn.tsx";
import { whenLeaving } from "../workspaces/leaving.tsx";
import { MovesProvider, type Moves } from "./moves.tsx";
import { enteringOf, planPick, type Entering, type Pick } from "./pick.ts";

export function WorkspaceRoute() {
  const { slug = "" } = useParams();
  const location = useLocation();
  const address = parseAddress(location.pathname);
  if (address.kind === "renamed") {
    return (
      <Navigate
        to={tenantPath(address.slug, address.to)}
        state={address.tab !== undefined ? { tab: address.tab } : undefined}
        replace
      />
    );
  }
  if (address.kind !== "tenant") {
    return (
      <Entry>
        <div className="entry-card">
          <h1>Not an address of this console</h1>
          <p className="lead">{address.kind === "invalid" ? address.reason : "The sign-in page is at /."}</p>
          <Link className="text-link" to="/">
            Go to the sign-in page
          </Link>
        </div>
      </Entry>
    );
  }
  return <Workspace slug={slug} />;
}

function Workspace({ slug }: { slug: string }) {
  const { origin, session, setSession } = usePage();
  const location = useLocation();
  const [tenant] = useLoad(slug, () => readTenant(origin, slug));
  // The profile's display name once it is read; the slug while it is not.
  const name = workspaceName(slug, tenant.state === "ready" ? tenant.value.name : undefined);
  if (tenant.state === "pending") {
    return (
      <Entry>
        <p className="faint" aria-busy="true">
          Opening {name}…
        </p>
      </Entry>
    );
  }
  if (tenant.state === "refused") {
    return (
      <Entry>
        <div className="entry-card">
          <h1>No workspace here</h1>
          <p className="lead">
            There is no workspace at <span className="mono">{consoleAddress(origin, slug)}</span>.
          </p>
          <Link className="text-link" to="/">
            Sign in somewhere else
          </Link>
        </div>
      </Entry>
    );
  }
  if (session === undefined) {
    return <WorkspaceSignIn tenant={tenant.value} />;
  }
  if (session.slug !== slug) {
    if (session.slug === "") {
      // Signed in under the platform provider with no workspace named: this address names one.
      return <Retarget slug={slug} />;
    }
    // A pick of a workspace the sign-in does not admit: its own sign-in page,
    // the session left with the workspace that does.
    const entering = enteringOf(location.state);
    if (entering?.signIn === true) {
      return <WorkspaceSignIn tenant={tenant.value} refused={entering.refused} />;
    }
    const other = workspaceName(session.slug);
    return (
      <Entry>
        <div className="entry-card">
          <h1>You are signed in to {other}</h1>
          <p className="lead">
            Entering {name} signs you out of {other} in this tab.
          </p>
          <div className="rows">
            <Button
              kind="primary"
              size="lg"
              block
              onClick={() => {
                setSession(undefined);
              }}
            >
              Enter {name}
            </Button>
            <Link className="btn lg block" to={tenantPath(session.slug)}>
              Back to {other}
            </Link>
          </div>
        </div>
      </Entry>
    );
  }
  return <Connected tenant={tenant.value} lane={session.lane} />;
}

function Retarget({ slug }: { slug: string }) {
  const { session, setSession } = usePage();
  useEffect(() => {
    if (session !== undefined && session.slug === "") {
      setSession({ ...session, slug });
    }
  }, [session, setSession, slug]);
  return (
    <Entry>
      <p className="faint" aria-busy="true">
        Entering {workspaceName(slug)}…
      </p>
    </Entry>
  );
}

/**
 * refusalText is the one sentence a refusal at the callout gets: the reason
 * the auth service kept for this connection's inbox token, read from the
 * refusal lookup (decision 0016), and where it kept none — the server answers
 * an authorization violation and nothing more — the two likely causes and
 * whom to ask.
 */
export function refusalText(err: unknown, name: string, refusal?: Refusal, inUse?: string): string {
  const said = refusalSentence(refusal, name, inUse);
  if (said !== undefined) {
    return said;
  }
  const text = describe(err);
  if (/authorization violation|authentication/i.test(text)) {
    return `${name} has no access rule for this account, or did not admit the sign-in for a reason it did not share. Ask an admin of ${name} to add you.`;
  }
  return text;
}

/** inUseName is the provider a workspace signs in with now, by its display name. */
function inUseName(tenant: TenantProfile): string {
  return tenant.provider !== undefined ? providerName(tenant.provider) : PLATFORM_NAME;
}

function Connected({ tenant, lane }: { tenant: TenantProfile; lane: "oidc" | "github" }) {
  const { origin, platform, session, setSession } = usePage();
  const navigate = useNavigate();
  const location = useLocation();
  const conn = useBus(origin, platform, tenant, lane);
  const { state } = conn;
  const name = workspaceName(tenant.slug, tenant.name);
  // Sign-out forgets the tokens, then ends the session at the provider where
  // it can (03-signing-in.md § the browser); the provider returns the browser
  // to the sign-in page, so only a sign-out it cannot end there stays here.
  const signOut = () => {
    const to =
      session === undefined
        ? undefined
        : endSessionUrl(origin, providerOf(platform, tenant, session), session);
    setSession(undefined);
    if (to !== undefined) {
      window.location.assign(to);
      return;
    }
    void navigate(tenantPath(tenant.slug), { replace: true });
  };
  // The workspace menu's moves (01-the-console.md § the shape: the workspace
  // menu). Only a sign-in under the platform provider has memberships and
  // reaches Choose a workspace: a token from the workspace's own provider is
  // for that workspace alone, so leaving it is that workspace's sign-in page.
  const onPlatform =
    session !== undefined && providerOf(platform, tenant, session) === platform.platform_provider;
  // The session as it stands now: a refresh since this page rendered saved a
  // newer credential than the one this render holds.
  const current = () => loadSession() ?? session;
  // *All workspaces* and *Create a workspace* ask for the identity plane, so
  // Choose a workspace stands for one membership too (hq decision 0072).
  const toPlane = (state?: unknown) => {
    const s = current();
    if (s === undefined) {
      return;
    }
    forgetMemberships();
    setSession({ ...s, slug: "" });
    void navigate("/", { state: withPlaneAsk(state) });
  };
  const go = (p: Pick) => {
    if (p.kind === "home") {
      void navigate(tenantPath(tenant.slug));
      return;
    }
    const s = current();
    // A membership's pick is the consent: the session names the workspace
    // before the page moves, so its address places the person rather than asking.
    if (p.kind === "enter" && s !== undefined) {
      setSession({ ...s, slug: p.slug });
    }
    void navigate(tenantPath(p.slug), { state: p.state });
  };
  const memberships = async (signal?: AbortSignal) => {
    const s = current();
    if (s === undefined || !onPlatform) {
      return [];
    }
    const kept = keptMemberships(s);
    if (kept !== undefined) {
      return kept;
    }
    const read = await readMemberships(origin, platform, s.lane, undefined, signal);
    keepMemberships(s, read);
    return read;
  };
  const moves: Moves = {
    onPlatform,
    signUp: platform.sign_up,
    enter: (slug, member) => {
      whenLeaving(() => {
        if (member !== undefined || !onPlatform || slug === tenant.slug) {
          go(planPick(tenant.slug, slug, onPlatform && member === true));
          return;
        }
        // A child's row or *Open Northwind*: its Home only where it is one of the memberships.
        memberships().then(
          (ms) => {
            go(
              planPick(
                tenant.slug,
                slug,
                ms.some((m) => m.tenant === slug),
              ),
            );
          },
          () => {
            go(planPick(tenant.slug, slug, false));
          },
        );
      });
    },
    all: () => {
      whenLeaving(() => {
        toPlane();
      });
    },
    create: () => {
      whenLeaving(() => {
        toPlane(withForm(null, CREATE_FORM));
      });
    },
    elsewhere: () => {
      whenLeaving(() => {
        setSession(undefined);
        void navigate("/");
      });
    },
    memberships,
  };
  // A membership's pick the placement refused: back to the workspace the
  // person came from, and this workspace's sign-in page with the refusal's sentence.
  const picked = enteringOf(location.state);
  const refusedPick = state.kind === "refused" && picked?.from !== undefined ? picked.from : undefined;
  useEffect(() => {
    if (refusedPick === undefined || state.kind !== "refused") {
      return;
    }
    const s = loadSession();
    const sentence = refusalText(state.err, name, state.refusal, inUseName(tenant));
    if (s !== undefined) {
      setSession({ ...s, slug: refusedPick });
    }
    const back: Entering = { signIn: true, refused: sentence };
    void navigate(tenantPath(tenant.slug), { replace: true, state: back });
  }, [refusedPick, state, name, tenant, setSession, navigate]);
  if (refusedPick !== undefined) {
    return (
      <Entry>
        <p className="faint" aria-busy="true">
          Entering {name}…
        </p>
      </Entry>
    );
  }
  if (state.kind === "connecting") {
    return (
      <Entry>
        <p className="faint" aria-busy="true">
          Entering {name}…
        </p>
      </Entry>
    );
  }
  if (state.kind === "refused" || state.kind === "ended") {
    const err = state.kind === "refused" ? state.err : new Error(state.reason);
    // A sign-in through a provider the workspace no longer uses: say which it
    // uses now and offer it (01-the-console.md § the states between; decision 0055).
    const moved = state.kind === "refused" && state.refusal?.reason === "provider-not-in-use";
    const inUse = inUseName(tenant);
    const continueWith = () => {
      const choice = choices(origin, platform, tenant)[0];
      setSession(undefined);
      if (choice === undefined) {
        void navigate(tenantPath(tenant.slug), { replace: true });
        return;
      }
      void begin(origin, choice, tenantPath(tenant.slug)).then((to) => {
        window.location.assign(to);
      });
    };
    return (
      <Entry>
        <div className="entry-card">
          <h1>{state.kind === "ended" ? "Session ended" : "Not allowed"}</h1>
          <p className="lead">
            {refusalText(err, name, state.kind === "refused" ? state.refusal : undefined, inUse)}
          </p>
          <div className="rows">
            {moved ? (
              <Button kind="primary" size="lg" block onClick={continueWith}>
                Continue with {inUse}
              </Button>
            ) : (
              <Button kind="primary" size="lg" block onClick={conn.again}>
                Try again
              </Button>
            )}
            <Button size="lg" block onClick={signOut}>
              Sign in again
            </Button>
          </div>
        </div>
      </Entry>
    );
  }
  return (
    <ConnectionProvider value={conn}>
      <MovesProvider value={moves}>
        <Shell tenant={tenant} who={state.who} signOut={signOut} />
      </MovesProvider>
    </ConnectionProvider>
  );
}
